import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useEffect, useRef, useState,
} from 'react';
import { ContentCategory } from '../../../common/enums';
import { TopTradeContentCategory } from './TopTradeContentCategory';
import { RewardsContentCategory } from './RewardsContentCategory';
import { VideoContentCategory } from './VideoContentCategory';
import { PodcastContentCategory } from './PodcastContentCategory';
import { RecommendedContentCategory } from './RecommendedContentCategory';
import { Topic1ContentCategory } from './Topic1ContentCategory';
import { Topic2ContentCategory } from './Topic2ContentCategory';
import { Topic3ContentCategory } from './Topic3ContentCategory';
import { Topic4ContentCategory } from './Topic4ContentCategory';
import { Topic5ContentCategory } from './Topic5ContentCategory';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { ContentFeedSection, FeedInteractionAction } from '../../../common/models/content/Content';
import { useTrackElementVisibilityInViewPort } from '../../../common/hooks/useTrackElementVisibilityInViewPort';

export function ContentViewMoreWrapper() {
  const [searchParams] = useSearchParams();
  const [someContentLoadedInitially, setSomeContentLoadedInitially] = useState(false);
  const contentFeedEndMessageRef = useRef(null);
  const { trackFeedInteraction } = useCustomEventTracking();
  const trackElementVisibility = useTrackElementVisibilityInViewPort();
  const { t } = useTranslation();
  const topTrade = searchParams.get('topTradeName') ?? undefined;
  const sectionIndexParam = searchParams.get('sectionIndex') ?? undefined;
  const sectionNameParam = searchParams.get('sectionName') ?? undefined;
  const contentFeedSection = (sectionIndexParam !== undefined && sectionNameParam !== undefined ? {
    index: Number(sectionIndexParam),
    name: sectionNameParam,
  } : {}) as unknown as ContentFeedSection;
  const contentCategoryParam = searchParams.get('contentCategory');
  const contentCategory = Object.values(ContentCategory)
    .includes(contentCategoryParam as ContentCategory) ?
    (contentCategoryParam as ContentCategory) :
    undefined;

  // Track when first content exists on feed
  useEffect(() => {
    if (someContentLoadedInitially) {
      trackFeedInteraction(
        'View',
        'ContentStart',
        {
          action: FeedInteractionAction.Visit,
          feedVisit: new Date().toISOString(),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [someContentLoadedInitially]);

  useEffect(() => {
    const endMessageElement = contentFeedEndMessageRef.current as unknown as Element;

    const observers = trackElementVisibility([endMessageElement], () => {
      if (contentFeedEndMessageRef.current) {
        trackFeedInteraction(
          'View',
          'ContentEnd',
          {
            action: FeedInteractionAction.ReachEnd,
          },
        );
      }
    });
    // Clean up the observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contentTopics = {
    1: {
      categoryId: ContentCategory.TOPIC1,
      name: t('content.categories.marketing'),
    },
    2: {
      categoryId: ContentCategory.TOPIC2,
      name: t('content.categories.pricing'),
    },
    3: {
      categoryId: ContentCategory.TOPIC3,
      name: t('content.categories.leadGeneration'),
    },
    4: {
      categoryId: ContentCategory.TOPIC4,
      name: t('content.categories.hiring'),
    },
    5: {
      categoryId: ContentCategory.TOPIC5,
      name: t('content.categories.training'),
    },
  };

  switch (contentCategory as ContentCategory) {
    case ContentCategory.TOP_TRADE:
      if (topTrade) {
        return (
          <TopTradeContentCategory
            categoryId={ContentCategory.TOP_TRADE}
            keyProp={t('content.categories.topTrade')}
            topTrade={topTrade}
            setSomeContentLoadedInitially={setSomeContentLoadedInitially}
            viewMorePage={true}
            contentFeedSection={contentFeedSection}
          />
        );
      }
      return (
        <RecommendedContentCategory
          categoryId={ContentCategory.RECOMMENDED}
          keyProp={t('content.categories.recommended')}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={{} as unknown as ContentFeedSection} // this is a fallback case
        />
      );

    case ContentCategory.REWARDS:
      return (
        <RewardsContentCategory
          categoryId={ContentCategory.REWARDS}
          keyProp={t('content.categories.rewardsAndMore')}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.VIDEO:
      return (
        <VideoContentCategory
          categoryId={ContentCategory.VIDEO}
          keyProp={t('content.categories.video')}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.PODCAST:
      return (
        <PodcastContentCategory
          categoryId={ContentCategory.PODCAST}
          keyProp={t('content.categories.podcast')}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.TOPIC1:
      return (
        <Topic1ContentCategory
          categoryId={contentTopics[1].categoryId}
          topicName={contentTopics[1].name}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.TOPIC2:
      return (
        <Topic2ContentCategory
          categoryId={contentTopics[2].categoryId}
          topicName={contentTopics[2].name}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.TOPIC3:
      return (
        <Topic3ContentCategory
          categoryId={contentTopics[3].categoryId}
          topicName={contentTopics[3].name}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.TOPIC4:
      return (
        <Topic4ContentCategory
          categoryId={contentTopics[4].categoryId}
          topicName={contentTopics[4].name}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );

    case ContentCategory.TOPIC5:
      return (
        <Topic5ContentCategory
          categoryId={contentTopics[5].categoryId}
          topicName={contentTopics[5].name}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={contentFeedSection}
        />
      );
    default:
      return (
        <RecommendedContentCategory
          categoryId={ContentCategory.RECOMMENDED}
          keyProp={t('content.categories.recommended')}
          setSomeContentLoadedInitially={setSomeContentLoadedInitially}
          viewMorePage={true}
          contentFeedSection={
            contentCategory === ContentCategory.RECOMMENDED ? contentFeedSection :
              ({} as unknown as ContentFeedSection) // this is a fallback case
          }
        />
      );
  }
}
